/**
 * Formatação numérica compartilhada (pt-BR) — implementação canônica do app.
 *
 * Promovida de `pages/operacoes/utils/numberUtils.js` (que hoje é só um
 * re-export fino deste módulo). Convenção do projeto (CLAUDE.md §4): NUNCA
 * reimplementar formatação inline — importe daqui.
 */

const BR_INT = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 });
const BR_2 = new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const BR_1 = new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

/** "1.234" (inteiro com separador BR). */
export function FormatInt(n) {
    const v = Number(n);
    if (!Number.isFinite(v)) return '0';
    return BR_INT.format(v);
}

/** "1.234,56" (sempre 2 casas, SEM prefixo R$). */
export function FormatBrl(n) {
    const v = Number(n);
    if (!Number.isFinite(v)) return '0,00';
    return BR_2.format(v);
}

/**
 * Espaço NÃO-SEPARÁVEL entre o cifrão e o número.
 *
 * Com espaço comum, "R$ 1.234,56" quebra em duas linhas quando a coluna
 * aperta — o "R$" fica sozinho no fim de uma linha e o valor na seguinte, o
 * que dobra a altura da célula e, pior, atrapalha a leitura de uma coluna de
 * valores. Como todo dinheiro do melidash sai daqui, corrigir na origem
 * resolve a tabela inteira em vez de cada tela.
 */
const ESPACO_FIXO = ' ';

/** "R$ 1.234,56" (preço; 0 vira "R$ 0,00"). Não quebra linha entre R$ e valor. */
export function FormatPrice(n) {
    return `R$${ESPACO_FIXO}${FormatBrl(n)}`;
}

/**
 * "R$ 1.234,56" com guard de vazio: null/undefined/''/NaN → `blank` ('—').
 * É o formatador de dinheiro para DISPLAY em tabelas/cards (0 é um valor
 * legítimo e formata como "R$ 0,00"; só ausência vira blank).
 */
export function FormatMoney(n, { blank = '—' } = {}) {
    if (n === null || n === undefined || n === '') return blank;
    const v = Number(n);
    if (!Number.isFinite(v)) return blank;
    return `R$${ESPACO_FIXO}${FormatBrl(v)}`;
}

/** "12%" (inteiro). */
export function FormatPercent(n) {
    const v = Number(n);
    if (!Number.isFinite(v)) return '0%';
    return `${Math.round(v)}%`;
}

/** "1234,5" (1 casa) — usado pra avgPeriod. */
export function FormatDecimal1(n) {
    const v = Number(n);
    if (!Number.isFinite(v)) return '0,0';
    return BR_1.format(v);
}

/**
 * Bytes → "512 B" / "1.5 KB" / "2.3 MB". Inválido/<=0 → `blank` ('-').
 * (Unifica as 4 cópias do cluster E-12: FileUploader, WMS details/create, Health.)
 */
export function FormatFileSize(bytes, { blank = '-' } = {}) {
    const value = Number(bytes || 0);
    if (!Number.isFinite(value) || value <= 0) return blank;
    if (value < 1024) return `${value} B`;
    if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
    return `${(value / (1024 * 1024)).toFixed(1)} MB`;
}

/** Palavra no singular/plural conforme count ("1 item" / "2 itens"). */
export function Pluralize(count, singular, plural = null) {
    const n = Number(count || 0);
    return Math.abs(n) === 1 ? singular : plural || `${singular}s`;
}

/** "1.234 itens" — contagem formatada + palavra pluralizada (cluster E-13). */
export function CountLabel(count, singular, plural = null) {
    return `${FormatInt(count)} ${Pluralize(count, singular, plural)}`;
}

/**
 * Duração em SEGUNDOS → texto curto pt-BR: "2h 05min", "12min 30s", "45s".
 *
 * Existe aqui (e não na tela que precisou dela) porque duração já estava
 * reimplementada em dois lugares do app — CLAUDE.md §4 é explícito: formatação
 * compartilhada mora em `utils/`. Uma unidade só quando ela basta; duas quando
 * a segunda ainda muda a leitura (o minuto importa dentro de uma hora, o
 * segundo importa dentro de um minuto).
 *
 * Negativo/NaN/ausente → `blank`. ZERO é valor legítimo ("0s"), não ausência:
 * uma OS aceita há um instante tem tempo zero, não tempo desconhecido.
 */
export function FormatDurationSec(seconds, { blank = '—' } = {}) {
    if (seconds === null || seconds === undefined || seconds === '') return blank;
    const total = Number(seconds);
    if (!Number.isFinite(total) || total < 0) return blank;
    const whole = Math.floor(total);
    const hours = Math.floor(whole / 3600);
    const minutes = Math.floor((whole % 3600) / 60);
    const secs = whole % 60;
    if (hours > 0) return `${hours}h ${String(minutes).padStart(2, '0')}min`;
    if (minutes > 0) return `${minutes}min ${String(secs).padStart(2, '0')}s`;
    return `${secs}s`;
}

/**
 * Duração em MINUTOS, com dia como maior unidade — a régua de um período de
 * campanha, que dura dias e não horas ("96h 00min" do FormatDurationSec é
 * ilegível para "4 dias"). Mesma regra da irmã: uma unidade só quando ela
 * basta, duas quando a segunda ainda muda a leitura.
 *
 * Negativo/NaN/ausente → `blank`. Zero é legítimo ("0 min"): período que abriu
 * e fechou na mesma leitura do sync existe.
 */
export function FormatDurationMin(minutes, { blank = '—' } = {}) {
    if (minutes === null || minutes === undefined || minutes === '') return blank;
    const total = Number(minutes);
    if (!Number.isFinite(total) || total < 0) return blank;
    const whole = Math.floor(total);
    const days = Math.floor(whole / 1440);
    const hours = Math.floor((whole % 1440) / 60);
    const mins = whole % 60;
    if (days > 0) return hours > 0 ? `${days}d ${hours}h` : `${days}d`;
    if (hours > 0) return mins > 0 ? `${hours}h ${mins}min` : `${hours}h`;
    return `${mins}min`;
}

/**
 * Duração por extenso, em português, para frase corrida: "3 dias",
 * "1 mês e 2 dias", "5 horas e 10 min", "menos de 1 min".
 *
 * Complementa `FormatDurationMin` ("3d 4h", bom para tabela): aqui o leitor é
 * o dono lendo "ficou 3 dias na campanha X", então só as DUAS maiores unidades
 * entram e "mês" é 30 dias — precisão de calendário não importa nessa escala.
 */
export function FormatDurationHuman(minutes, { blank = '—' } = {}) {
    if (minutes === null || minutes === undefined || minutes === '') {
        return blank;
    }
    const total = Number(minutes);
    if (!Number.isFinite(total) || total < 0) {
        return blank;
    }
    const whole = Math.floor(total);
    if (whole < 1) {
        return 'menos de 1 min';
    }
    const months = Math.floor(whole / 43200);
    const days = Math.floor((whole % 43200) / 1440);
    const hours = Math.floor((whole % 1440) / 60);
    const mins = whole % 60;
    const plural = (n, um, varios) => `${n} ${n === 1 ? um : varios}`;
    const partes = [];
    if (months > 0) {
        partes.push(plural(months, 'mês', 'meses'));
    }
    if (days > 0) {
        partes.push(plural(days, 'dia', 'dias'));
    }
    if (months === 0 && hours > 0) {
        partes.push(plural(hours, 'hora', 'horas'));
    }
    if (months === 0 && days === 0 && mins > 0) {
        partes.push(`${mins} min`);
    }
    return partes.slice(0, 2).join(' e ');
}

/**
 * Número → texto pt-BR com casas controladas, para PREENCHER input (e para
 * display quando a quantidade de casas varia): custo com até 10 casas e
 * `grouping: false` ("11,6743"), peso com até 3 ("0,15"). Aceita string
 * numérica (DECIMAL do mysql2 chega como string). Vazio/NaN → `blank` ('').
 */
export function FormatDecimalBr(n, { minFractionDigits = 0, maxFractionDigits = 2, grouping = true, blank = '' } = {}) {
    if (n === null || n === undefined || n === '') {
        return blank;
    }
    const v = Number(n);
    if (!Number.isFinite(v)) {
        return blank;
    }
    return new Intl.NumberFormat('pt-BR', {
        minimumFractionDigits: minFractionDigits,
        maximumFractionDigits: Math.max(minFractionDigits, maxFractionDigits),
        useGrouping: grouping,
    }).format(v);
}

/**
 * Input decimal do usuário → number. '' → null (campo vazio); inválido → NaN
 * (o submit acusa). Aceita vírgula decimal ("2,50") e milhar BR ("1.500,00");
 * sem vírgula, o ponto é decimal ("2.5").
 *
 * Promovida de `components/warehouse/locationFields.js` (que hoje reexporta):
 * o cadastro de produto usa o mesmo parse de preço/custo/peso.
 */
export function ParseDecimalInput(raw) {
    const text = String(raw ?? '').trim();
    if (!text) {
        return null;
    }
    const normalized = text.includes(',') ? text.replace(/\./g, '').replace(',', '.') : text;
    const value = Number(normalized);
    return Number.isFinite(value) ? value : Number.NaN;
}

/** Input inteiro não-negativo → number. '' → null; inválido → NaN. */
export function ParseIntegerInput(raw) {
    const text = String(raw ?? '').trim();
    if (!text) {
        return null;
    }
    return /^\d+$/.test(text) ? Number(text) : Number.NaN;
}

/** Round seguro pra inteiro. */
export function SafeRound(n) {
    const v = Number(n);
    if (!Number.isFinite(v)) return 0;
    return Math.round(v);
}

/** Clamp [min, max]. */
export function Clamp(n, min, max) {
    return Math.min(Math.max(Number(n), min), max);
}
